/**
 * React Query hooks — centralised data layer for the Paxley dashboard.
 *
 * Goals:
 *  1. Consistent query-key management (single source of truth).
 *  2. Optimistic updates where possible (suppress mutation).
 *  3. Prefetch helpers for hover-ahead loading.
 *  4. Auto-invalidation across related caches.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  listRepos,
  listRepoFindings,
  triggerScan,
  suppressFinding,
  getRepoMetrics,
  getScanStatus,
  listSavedViews,
  createSavedView,
  deleteSavedView,
  getPriorityQueue,
  syncReposFromGitHub,
  type RepoSummary,
  type FindingRecord,
  type MetricsResponse,
  type ScanStatusResponse,
  type TriggerScanResponse,
  type SavedView,
  type PriorityFinding,
  type SyncReposResult,
} from "./client";

// ─── Query Key Factory ────────────────────────────────────────────

export const queryKeys = {
  repos: {
    all: ["repos"] as const,
    detail: (id: string) => ["repos", id] as const,
    findings: (id: string) => ["repoFindings", id] as const,
    metrics: (id: string, days?: number) => ["repoMetrics", id, days ?? 90] as const,
    settings: (id: string) => ["repo-settings", id] as const,
    priority: (id: string) => ["priorityQueue", id] as const,
  },
  jobs: {
    status: (id: string) => ["jobStatus", id] as const,
  },
  views: {
    all: ["savedViews"] as const,
  },
  settings: {
    org: ["settings"] as const,
  },
} as const;

// ─── Repo Queries ─────────────────────────────────────────────────

export function useRepos(opts?: Partial<UseQueryOptions<RepoSummary[]>>) {
  return useQuery({
    queryKey: queryKeys.repos.all,
    queryFn: listRepos,
    staleTime: 30_000,
    ...opts,
  });
}

export function useRepoFindings(
  repoId: string,
  filters?: {
    severity?: string;
    tool?: string;
    status?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: [...queryKeys.repos.findings(repoId), filters ?? {}],
    queryFn: () => listRepoFindings(repoId, filters),
    enabled: Boolean(repoId),
    staleTime: 15_000,
  });
}

export function useRepoMetrics(repoId: string, days = 90) {
  return useQuery({
    queryKey: queryKeys.repos.metrics(repoId, days),
    queryFn: () => getRepoMetrics(repoId, days),
    enabled: Boolean(repoId),
    staleTime: 60_000, // metrics are less volatile
  });
}

export function usePriorityQueue(repoId: string, opts?: { limit?: number; minScore?: number }) {
  return useQuery({
    queryKey: [...queryKeys.repos.priority(repoId), opts],
    queryFn: () => getPriorityQueue(repoId, opts),
    enabled: Boolean(repoId),
    staleTime: 30_000,
  });
}

// ─── Saved Views ──────────────────────────────────────────────────

export function useSavedViews() {
  return useQuery({
    queryKey: queryKeys.views.all,
    queryFn: listSavedViews,
    staleTime: 60_000,
  });
}

export function useCreateView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (view: Parameters<typeof createSavedView>[0]) => createSavedView(view),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.views.all }),
  });
}

export function useDeleteView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.views.all }),
  });
}

// ─── Scan Job ─────────────────────────────────────────────────────

export function useScanStatus(jobId: string | null) {
  return useQuery({
    queryKey: queryKeys.jobs.status(jobId || ""),
    queryFn: () => getScanStatus(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data as ScanStatusResponse | undefined;
      if (!data) return 2000;
      return data.status === "completed" || data.status === "failed" ? false : 2000;
    },
  });
}

// ─── Mutations ────────────────────────────────────────────────────

export function useTriggerScan(repoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { scanType?: "baseline" | "pr"; branch?: string }) =>
      triggerScan(repoId, opts),
    onSuccess: () => {
      // Will be refreshed via scan-complete callback; just bump repos stale
      qc.invalidateQueries({ queryKey: queryKeys.repos.all });
    },
  });
}

export function useSyncRepos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: syncReposFromGitHub,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.repos.all }),
  });
}

/**
 * Suppress finding with **optimistic update**.
 *
 * Immediately marks the finding as "suppressed" in the cache,
 * then rolls back on error.
 */
export function useSuppressFinding(repoId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      findingId,
      reason,
      expires_at,
    }: {
      findingId: string;
      reason: string;
      expires_at?: string | null;
    }) => suppressFinding(findingId, { reason, expires_at }),

    // Optimistic update — flip status to "suppressed" instantly
    onMutate: async ({ findingId, reason, expires_at }) => {
      const key = queryKeys.repos.findings(repoId);
      await qc.cancelQueries({ queryKey: key });

      const previous = qc.getQueryData(key);
      qc.setQueryData(key, (old: any) => {
        if (!old) return old;
        const findings: FindingRecord[] = Array.isArray(old) ? old : old.findings || [];
        const updated = findings.map((f: FindingRecord) =>
          f.id === findingId
            ? {
                ...f,
                status: "suppressed" as const,
                suppress_reason: reason,
                suppress_expires_at: expires_at ?? null,
              }
            : f
        );
        return Array.isArray(old) ? updated : { ...old, findings: updated };
      });

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      // Roll back
      if (ctx?.previous) {
        qc.setQueryData(queryKeys.repos.findings(repoId), ctx.previous);
      }
    },

    onSettled: () => {
      // Re-fetch to sync with server state
      qc.invalidateQueries({ queryKey: queryKeys.repos.findings(repoId) });
      qc.invalidateQueries({ queryKey: queryKeys.repos.all }); // repo counts
    },
  });
}

/**
 * Invalidate all caches related to a repo after scan completion.
 */
export function invalidateRepo(qc: QueryClient, repoId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.repos.findings(repoId) }),
    qc.invalidateQueries({ queryKey: queryKeys.repos.metrics(repoId) }),
    qc.invalidateQueries({ queryKey: queryKeys.repos.priority(repoId) }),
    qc.invalidateQueries({ queryKey: queryKeys.repos.all }),
  ]);
}

// ─── Prefetch Helpers ─────────────────────────────────────────────

/**
 * Prefetch findings for a repo — call this on hover over a repo card
 * to make navigation feel instant.
 */
export function prefetchRepoFindings(qc: QueryClient, repoId: string) {
  qc.prefetchQuery({
    queryKey: queryKeys.repos.findings(repoId),
    queryFn: () => listRepoFindings(repoId),
    staleTime: 15_000,
  });
}
